import { useState } from 'react';
import foodTrackingService, { FoodItem, NutritionInfo } from '../../services/foodTrackingService';
import FoodScanner from './FoodScanner';
import FoodVisionScanner from './FoodVisionScanner';
import AddProductModal from './AddProductModal';
import FoodHistoryModal from './FoodHistoryModal';

interface FoodLoggerProps {
  date: string;
  mealType?: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  onClose: () => void;
  onSuccess: () => void;
}

export default function FoodLogger({ date, mealType: initialMealType, onClose, onSuccess }: FoodLoggerProps) {
  const [step, setStep] = useState<'main' | 'details' | 'scanner' | 'vision'>('main');
  const [selectedFood, setSelectedFood] = useState<FoodItem | null>(null);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  
  // Form state
  const [mealType, setMealType] = useState<'breakfast' | 'lunch' | 'dinner' | 'snack'>(
    initialMealType || 'breakfast'
  );
  // grams: direct gram input (default = selectedFood.servingSize, set when food is selected)
  const [grams, setGrams] = useState(100);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  // Meal time - default to current time
  const [mealTime, setMealTime] = useState(() => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  });

  // Commented out for future use when search feature is re-enabled
  // const [searchQuery, setSearchQuery] = useState('');
  // const [searchResults, setSearchResults] = useState<FoodItem[]>([]);
  // const [loading, setLoading] = useState(false);

  // const handleSearch = async (e: React.FormEvent) => {
  //   e.preventDefault();
  //   if (!searchQuery.trim()) return;
  //   setLoading(true);
  //   try {
  //     const result = await foodTrackingService.searchFood(searchQuery);
  //     setSearchResults(result.foods);
  //   } catch (error: any) {
  //     alert(error.message || 'Eroare la căutarea alimentului');
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // const handleSelectFood = (food: FoodItem) => {
  //   setSelectedFood(food);
  //   setStep('details');
  // };

  const handleScanSuccess = (food: FoodItem) => {
    setSelectedFood(food);
    // Pre-fill grams with the food's serving size
    setGrams(food.servingSize || 100);
    setStep('details');
  };

  const calculateNutrition = (): NutritionInfo => {
    if (!selectedFood) {
      return { calories: 0, protein: 0, carbs: 0, fat: 0 };
    }

    // nutrition values are per 100g, scale by actual grams
    const multiplier = grams / 100;
    return {
      calories: Math.round(selectedFood.nutrition.calories * multiplier),
      protein: Math.round(selectedFood.nutrition.protein * multiplier * 10) / 10,
      carbs: Math.round(selectedFood.nutrition.carbs * multiplier * 10) / 10,
      fat: Math.round(selectedFood.nutrition.fat * multiplier * 10) / 10,
      fiber: selectedFood.nutrition.fiber 
        ? Math.round(selectedFood.nutrition.fiber * multiplier * 10) / 10 
        : undefined,
      sugar: selectedFood.nutrition.sugar 
        ? Math.round(selectedFood.nutrition.sugar * multiplier * 10) / 10 
        : undefined,
      sodium: selectedFood.nutrition.sodium 
        ? Math.round(selectedFood.nutrition.sodium * multiplier * 10) / 10 
        : undefined,
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFood) return;

    setSaving(true);
    try {
      // Combine date + mealTime into a full datetime string for the backend
      // Include timezone offset so the server stores the correct UTC time
      const [hours, minutes] = mealTime.split(':');
      const localDate = new Date(
        parseInt(date.split('-')[0]),
        parseInt(date.split('-')[1]) - 1,
        parseInt(date.split('-')[2]),
        parseInt(hours),
        parseInt(minutes),
        0
      );
      const consumedAt = localDate.toISOString();

      // Send grams directly as quantity; backend stores it and maps quantity→servingSize in response
      await foodTrackingService.createEntry({
        foodName: selectedFood.name,
        brand: selectedFood.brand,
        barcode: selectedFood.barcode,
        servingSize: grams,
        servingUnit: selectedFood.servingUnit || 'g',
        quantity: grams,
        nutrition: calculateNutrition(),
        mealType,
        date: consumedAt,
        notes: notes.trim() || undefined,
        imageUrl: selectedFood.imageUrl,
      });

      onSuccess();
      onClose();
    } catch (error: any) {
      alert(error.message || 'Eroare la salvarea mesei');
    } finally {
      setSaving(false);
    }
  };

  if (step === 'scanner') {
    return (
      <FoodScanner
        onScanSuccess={handleScanSuccess}
        onClose={() => setStep('main')}
        mealType={mealType}
        date={date}
        onDirectAdd={onSuccess}
      />
    );
  }

  if (step === 'vision') {
    return (
      <FoodVisionScanner
        onScanSuccess={handleScanSuccess}
        onClose={() => setStep('main')}
        mealType={mealType}
        date={date}
        onDirectAdd={onSuccess}
      />
    );
  }

  const totalNutrition = calculateNutrition();

  const getMealIcon = (type: string) => {
    const icons = {
      breakfast: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      lunch: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      dinner: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
        </svg>
      ),
      snack: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
        </svg>
      )
    };
    return icons[type as keyof typeof icons] || (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
      </svg>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center sm:p-4 overflow-hidden">
      <div className="bg-white rounded-t-xl sm:rounded-xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto overflow-x-hidden" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            {step === 'details' && <span className="text-primary-600">{getMealIcon(mealType)}</span>}
            {step === 'main' ? 'Adaugă Aliment' : 'Detalii Masă'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 'main' && (
            <>
              {/* Main 3 Action Buttons */}
              <div className="space-y-4 mb-6">
                <p className="text-gray-600 text-center mb-4">
                  Alege metoda de adăugare:
                </p>

                {/* AI Vision Scanner Button */}
                <button
                  onClick={() => setStep('vision')}
                  className="w-full px-6 py-5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-xl transition-all group"
                >
                  <div className="flex items-center justify-center gap-3 mb-2">
                    <svg className="w-7 h-7 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <h3 className="font-bold text-lg mb-1">Scanează Produs/Masă cu AI</h3>
                  <p className="text-sm text-blue-100">Fă o poză și AI identifică alimentul automat</p>
                </button>

                {/* Add Product Button */}
                <button
                  onClick={() => setShowAddProduct(true)}
                  className="w-full px-6 py-5 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-xl hover:shadow-xl transition-all group"
                >
                  <div className="flex items-center justify-center gap-3 mb-2">
                    <svg className="w-8 h-8 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  <h3 className="font-bold text-lg mb-1">Adaugă Produs Nou</h3>
                  <p className="text-sm text-orange-100">Manual sau cu ajutorul AI</p>
                </button>

                {/* Barcode Scan Button */}
                <button
                  onClick={() => setStep('scanner')}
                  className="w-full px-6 py-5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:shadow-xl transition-all group"
                >
                  <div className="flex items-center justify-center gap-3 mb-2">
                    <svg className="w-8 h-8 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
                      />
                    </svg>
                  </div>
                  <h3 className="font-bold text-lg mb-1">Scanează Barcode</h3>
                  <p className="text-sm text-green-100">Scanează codul de bare de pe ambalaj</p>
                </button>

                {/* History Button */}
                <button
                  onClick={() => setShowHistory(true)}
                  className="w-full px-6 py-4 bg-white border-2 border-purple-200 text-purple-700 rounded-xl hover:bg-purple-50 hover:border-purple-400 transition-all group flex items-center justify-center gap-3"
                >
                  <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center group-hover:bg-purple-200 transition-colors flex-shrink-0">
                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <div className="font-bold text-base">Din Istoric</div>
                    <div className="text-xs text-purple-500">Re-adaugă alimente consumate anterior</div>
                  </div>
                  <svg className="w-5 h-5 text-purple-400 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              {/* Hidden Search for Future Use - Commented Out */}
              {/* 
              <details className="mt-6">
                <summary className="cursor-pointer text-sm text-gray-600 hover:text-primary-600 text-center">
                  Sau caută în baza de date
                </summary>
                <div className="mt-4">
                  <form onSubmit={handleSearch}>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Caută aliment..."
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      />
                      <button
                        type="submit"
                        disabled={loading || !searchQuery.trim()}
                        className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors font-medium"
                      >
                        {loading ? 'Caută...' : 'Caută'}
                      </button>
                    </div>
                  </form>
                  {searchResults && searchResults.length > 0 && (
                    <div className="space-y-3 mt-4">
                      {searchResults.map((food, index) => (
                        <button
                          key={index}
                          onClick={() => handleSelectFood(food)}
                          className="w-full text-left p-4 border border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-all"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900">{food.name}</h4>
                              {food.brand && <p className="text-sm text-gray-600">{food.brand}</p>}
                              <p className="text-xs text-gray-500 mt-1">
                                {food.servingSize}{food.servingUnit} • {food.nutrition.calories} kcal
                              </p>
                            </div>
                            {food.imageUrl && (
                              <img src={food.imageUrl} alt={food.name} className="w-12 h-12 object-cover rounded ml-3" />
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </details>
              */}
            </>
          )}

          {step === 'details' && selectedFood && (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Selected Food Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">{selectedFood.name}</h3>
                    {selectedFood.brand && (
                      <p className="text-sm text-gray-600">{selectedFood.brand}</p>
                    )}
                  </div>
                  {selectedFood.imageUrl && (
                    <img
                      src={selectedFood.imageUrl}
                      alt={selectedFood.name}
                      className="w-16 h-16 object-cover rounded"
                    />
                  )}
                </div>
              </div>

              {/* Meal Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tip masă
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { value: 'breakfast', label: 'Mic Dejun', icon: (
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                    )},
                    { value: 'lunch', label: 'Prânz', icon: (
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                    )},
                    { value: 'dinner', label: 'Cină', icon: (
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                      </svg>
                    )},
                    { value: 'snack', label: 'Gustare', icon: (
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                      </svg>
                    )},
                  ].map((meal) => (
                    <button
                      key={meal.value}
                      type="button"
                      onClick={() => setMealType(meal.value as any)}
                      className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center ${
                        mealType === meal.value
                          ? 'border-primary-500 bg-primary-50 text-primary-600'
                          : 'border-gray-200 hover:border-gray-300 text-gray-600'
                      }`}
                    >
                      <div className="mb-1">{meal.icon}</div>
                      <div className="text-xs font-medium">{meal.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Meal Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ora mesei
                </label>
                <div className="flex items-center gap-3">
                  <div className="flex-1 relative">
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <input
                      type="time"
                      value={mealTime}
                      onChange={(e) => setMealTime(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 font-medium text-gray-900"
                    />
                  </div>
                </div>
              </div>

              {/* Grams input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cantitate (grame)
                </label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setGrams(g => Math.max(1, g - 10))}
                    className="w-10 h-10 rounded-full bg-red-100 text-red-600 font-bold text-xl flex items-center justify-center hover:bg-red-200 transition-colors flex-shrink-0"
                  >
                    −
                  </button>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={grams === 0 ? '' : grams}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/[^0-9]/g, '');
                      setGrams(raw === '' ? 0 : parseInt(raw, 10));
                    }}
                    placeholder="0"
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-center font-bold text-lg"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setGrams(g => g + 10)}
                    className="w-10 h-10 rounded-full bg-green-100 text-green-600 font-bold text-xl flex items-center justify-center hover:bg-green-200 transition-colors flex-shrink-0"
                  >
                    +
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1 text-center">
                  Valori nutriționale per 100g: {selectedFood.nutrition.calories} kcal
                </p>
              </div>

              {/* Nutrition Summary */}
              <div className="bg-primary-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Total nutriție</h4>
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <div className="text-2xl font-bold text-primary-600">
                      {totalNutrition.calories}
                    </div>
                    <div className="text-xs text-gray-600">kcal</div>
                  </div>
                  <div>
                    <div className="text-xl font-bold text-blue-600">
                      {totalNutrition.protein}g
                    </div>
                    <div className="text-xs text-gray-600">Proteine</div>
                  </div>
                  <div>
                    <div className="text-xl font-bold text-yellow-600">
                      {totalNutrition.carbs}g
                    </div>
                    <div className="text-xs text-gray-600">Carbohidrați</div>
                  </div>
                  <div>
                    <div className="text-xl font-bold text-orange-600">
                      {totalNutrition.fat}g
                    </div>
                    <div className="text-xs text-gray-600">Grăsimi</div>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notițe (opțional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  placeholder="Ex: La restaurant, porție mare..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setStep('main')}
                  className="flex-1 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Înapoi
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors font-medium"
                >
                  {saving ? 'Salvez...' : 'Adaugă Masă'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Add Product Modal */}
      {showAddProduct && (
        <AddProductModal
          onClose={() => setShowAddProduct(false)}
          onSuccess={() => {
            setShowAddProduct(false);
          }}
          mealType={mealType}
          date={date}
          onDirectAdd={onSuccess}
        />
      )}

      {/* Food History Modal */}
      {showHistory && (
        <FoodHistoryModal
          date={date}
          mealType={mealType}
          onClose={() => setShowHistory(false)}
          onSuccess={() => {
            onSuccess();
            onClose();
          }}
        />
      )}
    </div>
  );
}
